import React, { useState, useEffect } from 'react';
import { FaBlog, FaPlus, FaEdit, FaTrash, FaSearch, FaEye } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Blog management component for administrators
 * Allows creating, editing, and managing blog posts
 */
const BlogManagement = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { showSuccess, showError } = useToast();
  
  // Fetch blogs on component mount
  useEffect(() => {
    fetchBlogs();
  }, []);
  
  // Fetch blogs from API
  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/blogs');
      setBlogs(response.data.data || []);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      showError('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };
  
  // Delete blog post
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      try {
        await api.delete(`/admin/blogs/${id}`);
        showSuccess('Blog post deleted successfully');
        fetchBlogs();
      } catch (error) {
        console.error('Error deleting blog post:', error);
        showError('Failed to delete blog post');
      }
    }
  };
  
  // Filter blogs based on search term
  const filteredBlogs = blogs.filter((blog) => {
    const search = searchTerm.toLowerCase();
    return (
      blog.title.toLowerCase().includes(search) ||
      blog.author.toLowerCase().includes(search) ||
      blog.category.toLowerCase().includes(search)
    );
  });
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaBlog className="mr-2 text-primary" /> Blog Management
          </h1>
          <p className="text-gray-600 mt-1">
            Create and manage blog posts
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <a
            href="/admin/blogs/new"
            className="btn-primary flex items-center"
          >
            <FaPlus className="mr-2" /> Create New Post
          </a>
        </div>
      </div>
      
      {/* Search */}
      <div className="bg-white rounded-lg shadow-soft p-4 mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            className="form-input pl-10 py-2 w-full"
            placeholder="Search blog posts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      
      {/* Blogs Table */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-6 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-soft overflow-hidden">
          {filteredBlogs.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-500">No blog posts found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Author
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Published
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBlogs.map((blog) => (
                    <tr key={blog._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {blog.featuredImage ? (
                            <img
                              src={blog.featuredImage}
                              alt={blog.title}
                              className="h-10 w-10 rounded-md object-cover mr-3"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-md bg-gray-200 flex items-center justify-center mr-3">
                              <FaBlog className="text-gray-500" />
                            </div>
                          )}
                          <div className="text-sm font-medium text-gray-900">
                            {blog.title}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{blog.author}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {blog.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {new Date(blog.publishedAt || blog.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <a
                          href={`/blogs/${blog.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700 mr-3"
                          title="View"
                        >
                          <FaEye />
                        </a>
                        <a
                          href={`/admin/blogs/edit/${blog._id}`}
                          className="text-primary hover:text-primary-600 mr-3"
                          title="Edit"
                        >
                          <FaEdit />
                        </a>
                        <button
                          onClick={() => handleDelete(blog._id)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BlogManagement;
